/**
 * Visual canvas expansion for an Outpaint node's controls.
 * Dragging and typing update settings only — they never start a render.
 */
import { useRef, useState, type PointerEvent } from 'react'
import type { MergedParameter } from '@/lib/ai/parameter-merge'
import { imageUrlFromFlowNode } from '@/lib/canvas/style-transfer-roles'
import {
  PREVIEW_STAGE,
  aspectLabel,
  dragCorner,
  dragSide,
  expansionSpec,
  previewScale,
  pushExpansion,
  readExpansion,
  redoExpansion,
  sidesForAspect,
  undoExpansion,
  writeExpansion,
  type ExpansionHistory,
  type ExpansionSnapshot,
  type Size,
  type Sides,
} from '@/lib/canvas/outpaint-expansion'

const ASPECTS = ['16:9', '9:16', '1:1', '4:5', '3:2'] as const
const EDGES = ['left', 'right', 'top', 'bottom'] as const
const CORNERS = ['nw', 'ne', 'se', 'sw'] as const
const CORNER_LABEL = { nw: 'Top-left', ne: 'Top-right', se: 'Bottom-right', sw: 'Bottom-left' } as const

export function outpaintSourceUrl(input: {
  nodeId?: string
  handles?: readonly { id: string; type?: string }[]
  nodes?: readonly { id: string; type?: string | null; data?: unknown }[]
  edges?: readonly { target?: string | null; targetHandle?: string | null; source?: string | null }[]
}): string | undefined {
  if (!input.nodeId) return undefined
  const incoming = (input.edges ?? []).find(edge => edge.target === input.nodeId && edge.targetHandle
    && (input.handles ?? []).some(handle => handle.id === edge.targetHandle && handle.type !== 'output'))
  if (!incoming?.source) return undefined
  return imageUrlFromFlowNode((input.nodes ?? []).find(node => node.id === incoming.source))
}

interface OutpaintExpansionEditorProps {
  parameters: MergedParameter[]
  settings: Record<string, unknown>
  onPatch: (patch: Record<string, number | number[]>) => void
  sourceSize?: Size | null
  sourceUrl?: string
  disabled?: boolean
}

const snapshotOf = (sides: Sides, zoom: number): ExpansionSnapshot => ({
  sides: { ...sides },
  zoom,
})

export function OutpaintExpansionEditor({
  parameters,
  settings,
  onPatch,
  sourceSize,
  sourceUrl,
  disabled,
}: OutpaintExpansionEditorProps) {
  const spec = expansionSpec(parameters)
  const [measured, setMeasured] = useState<Size | null>(null)
  const natural = sourceSize ?? measured ?? { width: 1024, height: 1024 }
  const sized = Boolean(sourceSize || measured)
  const stageRef = useRef<HTMLDivElement>(null)
  const drag = useRef<{ kind: 'edge' | 'corner'; id: string; sides: Sides; zoom: number; x: number; y: number; px: number } | null>(null)
  const geometry = spec && natural ? readExpansion(spec, settings, natural) : null
  const [history, setHistory] = useState<ExpansionHistory>(() => ({
    past: [],
    present: snapshotOf(geometry?.sides ?? { left: 0, right: 0, top: 0, bottom: 0 }, geometry?.zoom ?? 0),
    future: [],
  }))
  const historyRef = useRef(history)
  historyRef.current = history

  if (!spec || !geometry) return null

  const commit = (next: ExpansionSnapshot, record: boolean) => {
    if (!spec) return
    if (record) {
      const pushed = pushExpansion(historyRef.current, next)
      historyRef.current = pushed
      setHistory(pushed)
    }
    const placed = readExpansion(spec, {
      [spec.names.left]: next.sides.left,
      [spec.names.right]: next.sides.right,
      [spec.names.top]: next.sides.top,
      [spec.names.bottom]: next.sides.bottom,
      ...(spec.names.zoom ? { [spec.names.zoom]: next.zoom } : {}),
    }, natural)
    onPatch(writeExpansion(spec, placed))
  }

  const restore = (direction: 'undo' | 'redo') => {
    const next = direction === 'undo' ? undoExpansion(historyRef.current) : redoExpansion(historyRef.current)
    historyRef.current = next
    setHistory(next)
    commit(next.present, false)
  }

  const scale = previewScale(geometry.output, stageBox(stageRef.current))
  const down = (kind: 'edge' | 'corner', id: string) => (event: PointerEvent<HTMLButtonElement>) => {
    if (disabled) return
    event.preventDefault()
    event.currentTarget.setPointerCapture?.(event.pointerId)
    drag.current = { kind, id, sides: geometry.sides, zoom: geometry.zoom, x: event.clientX, y: event.clientY, px: Math.max(1e-6, scale) }
  }
  const move = (event: PointerEvent<HTMLButtonElement>) => {
    const current = drag.current
    if (!current || disabled) return
    const dx = event.clientX - current.x
    const dy = event.clientY - current.y
    const sides = current.kind === 'edge'
      ? dragSide(current.id as keyof Sides, current.sides, current.id === 'left' || current.id === 'right' ? dx : dy, current.px, natural, spec.limits)
      : dragCorner(current.id, current.sides, dx, dy, current.px, natural, spec.limits)
    commit(snapshotOf(sides, current.zoom), false)
  }

  const endDrag = () => {
    const current = drag.current
    drag.current = null
    if (!current) return
    commit(snapshotOf(geometry.sides, geometry.zoom), true)
  }
  const setSide = (side: keyof Sides, value: number) => {
    const sides = { ...geometry.sides, [side]: value }
    commit(snapshotOf(sides, geometry.zoom), true)
  }

  return (
    <div data-testid="outpaint-expansion-editor" className="space-y-3">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-mono text-[11px] font-medium uppercase tracking-wider">Expansion</span>
        <output data-testid="outpaint-output-size" className="tabular-nums text-muted-foreground">
          {sized ? `${natural.width} × ${natural.height} → ` : 'Preview '}{geometry.output.width} × {geometry.output.height}
        </output>
      </div>
      <div
        ref={stageRef}
        data-testid="outpaint-canvas"
        className="relative h-40 w-full overflow-hidden rounded-md border border-dashed border-border bg-muted/40"
      >
        <Preview geometry={geometry} sourceUrl={sourceUrl} onMeasure={setMeasured} />
        {EDGES.map(side => (
          <button
            key={side}
            type="button"
            disabled={disabled}
            aria-label={`${side[0].toUpperCase()}${side.slice(1)} edge, ${geometry.sides[side]} px`}
            onPointerDown={down('edge', side)}
            onPointerMove={move}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            className="absolute z-10 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-border bg-background"
            style={edgeHandleStyle(side)}
          />
        ))}
        {CORNERS.map(corner => (
          <button
            key={corner}
            type="button"
            disabled={disabled}
            aria-label={`${CORNER_LABEL[corner]} corner, ${geometry.output.width} × ${geometry.output.height}`}
            onPointerDown={down('corner', corner)}
            onPointerMove={move}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            className="absolute z-10 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 border border-border bg-background"
            style={cornerHandleStyle(corner)}
          />
        ))}
      </div>
      <p data-testid="outpaint-aspect" className="text-[11px] text-muted-foreground">
        Output {aspectLabel(geometry.output.width, geometry.output.height)}. The original stays in place; only the new area is generated.
        {spec.names.zoom ? ' Zoom out shrinks it inside the original frame. Edge amounts add pixels outside that frame. Both are what gets sent.' : ' Edges add pixels. This model has no separate zoom.'}
      </p>
      <div role="radiogroup" aria-label="Aspect ratio" className="flex flex-wrap gap-1">
        {ASPECTS.map(aspect => (
          <button
            key={aspect}
            type="button"
            role="radio"
            disabled={disabled}
            onClick={() => {
              const next = sidesForAspect(aspect, natural, spec.limits)
              commit(snapshotOf(next.sides, 0), true)
            }}
            className="h-7 rounded-md border border-border px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            {aspect}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {EDGES.map(side => (
          <label key={side} className="flex items-center justify-between gap-2 text-xs">
            <span>{side[0].toUpperCase()}{side.slice(1)}</span>
            <input
              aria-label={side[0].toUpperCase() + side.slice(1)}
              type="number"
              min={0}
              max={spec.limits.maxSide}
              step={spec.limits.snap}
              disabled={disabled}
              value={geometry.sides[side]}
              onChange={(event) => {
                const value = Number(event.target.value)
                if (Number.isFinite(value)) setSide(side, value)
              }}
              className="h-8 w-20 rounded-md border border-border bg-background px-2 text-right tabular-nums"
            />
          </label>
        ))}
      </div>
      {spec.names.zoom && (
        <label className="flex items-center justify-between gap-2 text-xs">
          <span>Zoom out</span>
          <input
            aria-label="Zoom out"
            type="number"
            min={spec.limits.zoomMin}
            max={spec.limits.zoomMax}
            step={1}
            disabled={disabled}
            value={geometry.zoom}
            onChange={(event) => {
              const zoom = Number(event.target.value)
              if (Number.isFinite(zoom)) commit(snapshotOf(geometry.sides, zoom), true)
            }}
            className="h-8 w-20 rounded-md border border-border bg-background px-2 text-right tabular-nums"
          />
        </label>
      )}
      <div className="flex gap-1">
        <button type="button" aria-label="Undo expansion" disabled={disabled || history.past.length === 0} onClick={() => restore('undo')} className="h-8 rounded-md border border-border px-2 text-xs disabled:opacity-40">Undo</button>
        <button type="button" aria-label="Redo expansion" disabled={disabled || history.future.length === 0} onClick={() => restore('redo')} className="h-8 rounded-md border border-border px-2 text-xs disabled:opacity-40">Redo</button>
        <button
          type="button"
          aria-label="Reset expansion"
          disabled={disabled}
          onClick={() => commit(snapshotOf({ left: 0, right: 0, top: 0, bottom: 0 }, 0), true)}
          className="h-8 rounded-md border border-border px-2 text-xs"
        >
          Reset
        </button>
      </div>
    </div>
  )
}

function stageBox(node: HTMLDivElement | null): Size {
  const rect = node?.getBoundingClientRect()
  if (rect && rect.width > 1 && rect.height > 1) return { width: rect.width, height: rect.height }
  return PREVIEW_STAGE
}

function Preview({
  geometry,
  sourceUrl,
  onMeasure,
}: {
  geometry: { output: Size; image: { x: number; y: number; width: number; height: number } }
  sourceUrl?: string
  onMeasure: (size: Size) => void
}) {
  const output = geometry.output
  const box = (rect: { x: number; y: number; width: number; height: number }) => ({
    left: `${(rect.x / output.width) * 100}%`,
    top: `${(rect.y / output.height) * 100}%`,
    width: `${(rect.width / output.width) * 100}%`,
    height: `${(rect.height / output.height) * 100}%`,
  })
  return (
    <>
      <span aria-hidden className="absolute inset-0 bg-[repeating-linear-gradient(45deg,rgba(255,255,255,0.16)_0_6px,rgba(0,0,0,0.12)_6px_12px)]" />
      {sourceUrl ? (
        <img
          src={sourceUrl}
          alt="Original image"
          draggable={false}
          onLoad={(event) => {
            const img = event.currentTarget
            if (img.naturalWidth > 0) onMeasure({ width: img.naturalWidth, height: img.naturalHeight })
          }}
          className="absolute object-fill"
          style={box(geometry.image)}
        />
      ) : (
        <span aria-hidden className="absolute bg-background/80" style={box(geometry.image)} />
      )}
    </>
  )
}

function edgeHandleStyle(side: keyof Sides): { left: string; top: string } {
  if (side === 'left') return { left: '0%', top: '50%' }
  if (side === 'right') return { left: '100%', top: '50%' }
  if (side === 'top') return { left: '50%', top: '0%' }
  return { left: '50%', top: '100%' }
}

function cornerHandleStyle(corner: typeof CORNERS[number]): { left: string; top: string } {
  const x = corner === 'nw' || corner === 'sw' ? '0%' : '100%'
  const y = corner === 'nw' || corner === 'ne' ? '0%' : '100%'
  return { left: x, top: y }
}
